import { Link } from '../components/Link.tsx';
import { LegalPage } from '../components/marketing/LegalPage.tsx';
import { useT } from '../i18n.ts';

export function Terms() {
  const t = useT();

  return (
    <LegalPage
      title={t('terms.title')}
      lede="The agreement between you and On A Hill LLC for using Goalpost."
      effectiveDate="September 18, 2026"
    >
      <h2 id="agreement">The agreement</h2>
      <p>
        These terms govern your use of Goalpost, operated by On A Hill LLC
        (“we”, “us”). By creating an account or using the service you agree to
        them and to the <Link to="/privacy">privacy policy</Link>. If you use
        Goalpost on behalf of an organization, you confirm you can bind it to
        these terms, and “you” includes that organization.
      </p>

      <h2 id="accounts">Accounts and workspaces</h2>
      <p>
        You must be at least 16 to use Goalpost. Keep your sign-in details to
        yourself and tell us if you think your account has been compromised.
        A workspace is administered by its owners: they decide who joins, which
        teams have access to which documents, and what the workspace pays for,
        and they are responsible for those choices.
      </p>

      <h2 id="content">Your content</h2>
      <p>
        You own what you put into Goalpost. You give us the permission we need
        to host, store, back up, display, and transmit it so the service works,
        including to the teammates and connected apps you share it with. You are
        responsible for having the rights to what you upload and for what your
        workspace members do with it. We do not claim ownership of your content
        and do not use it to train machine-learning models.
      </p>

      <h2 id="use">Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>break the law, or infringe anyone’s rights, through the service;</li>
        <li>probe, overload, or interfere with the service or other users’ workspaces;</li>
        <li>try to get access you have not been granted, or share credentials to get around seat limits;</li>
        <li>upload malware or content you have no right to share.</li>
      </ul>

      <h2 id="ai">Connected apps and AI assistants</h2>
      <p>
        You may connect third-party apps, including AI assistants, to your
        account through OAuth. A connected app acts as you within the workspace
        you chose for it, and what it does is your responsibility, the same as
        if you had done it yourself. Review the consent screen before allowing
        a connection, only connect apps you trust, and revoke any connection
        you no longer want from <strong>Profile → Connected apps</strong>.
        Third-party apps are governed by their own terms; we are not
        responsible for them.
      </p>

      <h2 id="billing">Plans and billing</h2>
      <p>
        Free workspaces cost nothing. Pro workspaces are billed per member,
        monthly or yearly, through Stripe, at the prices shown on the{' '}
        <Link to="/pricing">pricing page</Link> when you subscribe. Seats are
        adjusted as members join and leave. You can cancel at any time from the
        workspace’s Subscription page; the workspace stays on Pro until the end
        of the period already paid for and then returns to Free. We will give at
        least 30 days’ notice before a price change takes effect on an existing
        subscription. Fees are non-refundable except where the law requires
        otherwise.
      </p>

      <h2 id="service">The service</h2>
      <p>
        Goalpost is under active development. We may add, change, or remove
        features, and we may impose limits on Free workspaces. We will try to
        give notice of changes that remove something you rely on. We do not
        promise uninterrupted availability, but we do keep backups and work to
        restore service quickly when something breaks.
      </p>

      <h2 id="termination">Ending the agreement</h2>
      <p>
        You can delete your account at any time by emailing{' '}
        <a href="mailto:info@onahill.co">info@onahill.co</a>. We may
        suspend or close an account that breaks these terms, or that has been
        inactive for a long time, and will give notice where practical. When a
        workspace is deleted its content is removed, subject to the retention
        described in the privacy policy.
      </p>

      <h2 id="disclaimer">Disclaimers and liability</h2>
      <p>
        Goalpost is provided “as is” and “as available”, without warranties of
        any kind, express or implied, including fitness for a particular purpose
        and non-infringement. To the fullest extent the law allows, On A Hill
        LLC is not liable for indirect, incidental, special, or consequential
        damages, or for lost profits or data, arising from your use of the
        service, and our total liability for any claim is limited to the amount
        you paid us in the twelve months before the claim arose. Some
        jurisdictions do not allow these limitations, in which case they apply
        only as far as permitted.
      </p>

      <h2 id="law">Governing law</h2>
      <p>
        These terms are governed by the laws of the United States and of the
        state in which On A Hill LLC is organized, without regard to
        conflict-of-law rules. Any dispute will be brought in the courts of that
        state.
      </p>

      <h2 id="changes">Changes to these terms</h2>
      <p>
        When these terms change we update the date at the top. For a material
        change we will email account holders at least 14 days before it takes
        effect. Continuing to use Goalpost after that date means you accept the
        new terms.
      </p>

      <h2 id="contact">Contact</h2>
      <p>
        On A Hill LLC —{' '}
        <a href="mailto:info@onahill.co">info@onahill.co</a>
      </p>
    </LegalPage>
  );
}
